import {
  PaymentFrequencyEnum,
  PaymentFrequencyType,
  PaymentPlanType,
} from 'QuoteApp/services/endpoints/GetPaymentDetails';

export const isPaymentFrequencyType = (
  value: unknown
): value is PaymentFrequencyType => {
  if (typeof value !== 'object' || value === null) return false;

  const requiredKeys: [keyof PaymentFrequencyType, string][] = [
    ['identifier', 'string'],
    ['label', 'string'],
    ['description', 'string'],
    ['type', 'string'],
    ['numberOfPayments', 'number'],
    ['paymentPlanEnum', 'string'],
    ['paymentPlanId', 'string'],
  ];

  return requiredKeys.every(
    ([key, type]) => key in value && typeof (value as any)[key] === type
  );
};

const buildPaymentPlanDisplayName = (
  identifier: PaymentFrequencyEnum,
  numberOfPayments: number = 0
) => {
  if (identifier === 'in_full') {
    return 'Pay in Full';
  }

  if (identifier === 'semi_monthly') {
    return 'Semi-monthly payments';
  }

  if (identifier === 'monthly' && numberOfPayments === 6) {
    return 'Monthly payments';
  } else {
    return `${numberOfMonthlyPaymentsInWords(numberOfPayments)} payments`;
  }
};

export const getPaymentPlanDisplayName = (
  paymentPlan: PaymentPlanType | PaymentFrequencyType
) => {
  if (isPaymentFrequencyType(paymentPlan)) {
    return buildPaymentPlanDisplayName(
      paymentPlan.identifier,
      paymentPlan.numberOfPayments
    );
  }

  return buildPaymentPlanDisplayName(
    paymentPlan.paymentFrequencyIdentifier,
    paymentPlan?.paymentSchedule?.length
  );
};

export const numberOfMonthlyPaymentsInWords = (numberOfPayments: number) => {
  const DEFAULT_VALUE = 'Monthly payments';
  const MAX_MONTHLY_NUMBER_OF_PAYMENTS = 5;

  if (
    numberOfPayments <= 0 ||
    numberOfPayments > MAX_MONTHLY_NUMBER_OF_PAYMENTS
  ) {
    return DEFAULT_VALUE;
  }

  return new Intl.NumberFormat('en-US', { style: 'decimal' })
    .formatToParts(numberOfPayments)
    .map(({ type, value }) =>
      type === 'integer' ? toWords(value) : DEFAULT_VALUE
    )
    .join('');
};

const toWords = (num: string): string => {
  const words: Record<string, string> = {
    1: 'One',
    2: 'Two',
    3: 'Three',
    4: 'Four',
    5: 'Five',
  };

  return words[num] || num;
};
